import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useGetSuperadminDashboard, useListAllGyms } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { TrendingUp, Building2, Users, DollarSign, Calendar, BarChart3, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";
import { format } from "date-fns";

interface MRRSnapshot {
  id: string;
  date: string;
  totalMRR: number;
  premiumGymCount: number;
  locationAddonMRR: number;
  newMRR: number;
  expansionMRR: number;
  churnedMRR: number;
  netMRRChange: number;
  freeToPremiumConversionsThisPeriod: number;
}

export default function SuperadminAnalytics() {
  const { data: dashboard, isLoading } = useGetSuperadminDashboard();
  const { data: gyms } = useListAllGyms();
  const [mrrSnapshots, setMrrSnapshots] = useState<MRRSnapshot[]>([]);
  const [mrrLoading, setMrrLoading] = useState(true);

  const stats = (dashboard || {}) as any;
  const premiumGyms = (gyms || []).filter((g: any) => g.plan === "PREMIUM").length;

  useEffect(() => {
    apiFetch("/analytics/mrr-snapshots?limit=90")
      .then((data: unknown) => setMrrSnapshots(data as MRRSnapshot[]))
      .catch(() => {})
      .finally(() => setMrrLoading(false));
  }, []);

  const latestMRR = mrrSnapshots.length > 0 ? mrrSnapshots[mrrSnapshots.length - 1] : null;

  const chartData = mrrSnapshots.map((s) => ({
    date: format(new Date(s.date), "MMM d"),
    mrr: s.totalMRR,
    newMRR: s.newMRR,
    churnedMRR: s.churnedMRR,
  }));

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Platform Analytics</h1>
          <p className="text-muted-foreground">Overview of OPEN MAT platform metrics</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs flex items-center gap-1"><DollarSign className="w-3 h-3" /> Platform Revenue</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(stats.totalRevenue || 0)}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Total Gyms</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{stats.totalGyms || (gyms || []).length}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">{premiumGyms} premium</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs flex items-center gap-1"><Users className="w-3 h-3" /> Total Users</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{stats.totalUsers || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Total Sessions</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{stats.totalSessions || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs flex items-center gap-1"><DollarSign className="w-3 h-3" /> Current MRR</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(latestMRR?.totalMRR || 0)}</CardTitle>
            </CardHeader>
            <CardContent>
              {latestMRR && (
                <p className={`text-xs font-mono ${latestMRR.netMRRChange >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {latestMRR.netMRRChange >= 0 ? "+" : ""}{formatCurrency(latestMRR.netMRRChange)} net change
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Premium Gyms</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{latestMRR?.premiumGymCount || premiumGyms}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">New MRR</CardDescription>
              <CardTitle className="text-3xl font-mono text-green-400">{formatCurrency(latestMRR?.newMRR || 0)}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Churned MRR</CardDescription>
              <CardTitle className="text-3xl font-mono text-red-400">{formatCurrency(latestMRR?.churnedMRR || 0)}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> MRR Trend
            </CardTitle>
            <CardDescription>Monthly Recurring Revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            {mrrLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : chartData.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No MRR data yet. Snapshots are recorded daily.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e8c848" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#e8c848" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" fontSize={12} />
                  <YAxis stroke="rgba(255,255,255,0.3)" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px" }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, "MRR"]}
                  />
                  <Area type="monotone" dataKey="mrr" stroke="#e8c848" fill="url(#mrrGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Revenue by Gym</CardTitle>
            </CardHeader>
            <CardContent>
              {(gyms || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No data yet</p>
              ) : (
                <div className="space-y-4">
                  {(gyms || []).map((gym: any) => {
                    const maxRev = Math.max(...(gyms || []).map((g: any) => g.totalRevenue || 1));
                    const pct = ((gym.totalRevenue || 0) / maxRev) * 100;
                    return (
                      <div key={gym.id} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-white font-medium">{gym.name}</span>
                          <span className="font-mono text-primary">{formatCurrency(gym.totalRevenue || 0)}</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-primary/50 rounded-full transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Platform Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Active Sessions</p>
                    <p className="text-2xl font-mono text-white">{stats.monthlyActiveSessions || 0}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">New Users (30d)</p>
                    <p className="text-2xl font-mono text-white">{stats.newUsersThisMonth || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Platform Fee Collected</p>
                    <p className="text-2xl font-mono text-primary">{formatCurrency(stats.platformFeeCollected || 0)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
                {latestMRR && latestMRR.freeToPremiumConversionsThisPeriod > 0 && (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Free → Premium Conversions (30d)</p>
                      <p className="text-2xl font-mono text-green-400">{latestMRR.freeToPremiumConversionsThisPeriod}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-400" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
